/**
 * Thin fetch client for the Rootsight API.
 *
 * The API returns a stable machine-readable `code` with every error; the UI
 * translates that code rather than displaying server text, so nothing on the
 * server has to know the operator's language.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId?: string;
    /** The id of the offending row, on batch and conflict errors. */
    target?: string;
  };
}

/** ApiError carries the fields the UI needs to translate and report a failure. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /** Seconds the server asked the caller to wait (Retry-After), when it did. */
  readonly retryAfterSeconds: number | undefined;
  /** The row a batch or conflict error is about (a stale box, an occupying box). */
  readonly target: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    retryAfterSeconds?: number,
    target?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.retryAfterSeconds = retryAfterSeconds;
    this.target = target;
  }

  /** True when the session is missing or expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export interface ApiClientOptions {
  /** Base URL for the API, e.g. "/api/v1". */
  baseUrl?: string;
  /**
   * Called when the session is gone for good: a request failed with 401 and
   * the one refresh attempt did not recover it. Each SPA decides what to do:
   * the console navigates to the login SPA, the login SPA ignores it.
   */
  onUnauthenticated?: () => void;
}

export type QueryParamValue = string | number | boolean;

/** Query parameters. An array is sent as the parameter repeated once per
 * item (`state=a&state=b`), which is how the API reads a multi-valued filter. */
export type QueryParams = Record<string, QueryParamValue | readonly QueryParamValue[] | undefined>;

/** Per-call options. Deliberately narrower than RequestInit: method, body and
 * credentials are the client's business, not the caller's. */
export interface RequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

interface InternalRequest extends RequestOptions {
  params?: QueryParams;
  body?: unknown;
}

/**
 * Endpoints whose 401 is an answer, not an expired session: they are how a
 * session is made, ended or renewed, so refreshing on them would loop.
 */
const SESSION_ENDPOINTS: readonly string[] = ['/auth/refresh', '/auth/login', '/auth/logout'];

/**
 * A 401 with one of these codes means "that credential was wrong", not "your
 * session lapsed" (a wrong current password on the change-password form, for
 * instance). Refreshing or logging out on it would be the wrong reaction.
 */
const CREDENTIAL_REJECTIONS: readonly string[] = ['invalid_credentials', 'too_many_attempts'];

/** Name of the cross-tab lock that serialises session refreshes. */
const REFRESH_LOCK = 'rootsight-auth-refresh';

/** ApiClient is the single place that knows how to talk to the backend. */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly onUnauthenticated: (() => void) | undefined;
  /** The refresh in flight, shared by every request that hit a 401 meanwhile. */
  private refreshing: Promise<boolean> | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/$/, '');
    this.onUnauthenticated = options.onUnauthenticated;
  }

  get<T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, { ...options, ...(params ? { params } : {}) });
  }

  /** post sends body as JSON, or as multipart when it is a FormData. */
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body });
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, { ...options });
  }

  private async request<T>(method: string, path: string, options: InternalRequest): Promise<T> {
    let response = await this.send(method, path, options);

    if (response.status === 401 && !SESSION_ENDPOINTS.includes(path)) {
      const rejection = await toApiError(response.clone());
      if (!CREDENTIAL_REJECTIONS.includes(rejection.code)) {
        // The 15-minute access cookie lapsed (or the session was revoked).
        // Renew once and replay; a second 401 is final.
        response = await this.recover(method, path, options, response);
        if (response.status === 401) {
          const final = await toApiError(response.clone());
          if (!CREDENTIAL_REJECTIONS.includes(final.code)) {
            this.onUnauthenticated?.();
          }
        }
      }
    }

    if (!response.ok) {
      throw await toApiError(response);
    }

    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /**
   * recover replays a request that got a 401, renewing the session if needed.
   *
   * Refresh tokens are single-use with reuse detection, so two tabs refreshing
   * with the same cookie would look like a replay and revoke the whole
   * session. The Web Locks API serialises the refresh across tabs; inside the
   * lock the original request goes first, because another tab may already
   * have renewed the cookie. Without Web Locks it falls back to refreshing
   * directly (single-flight within this tab only).
   */
  private async recover(
    method: string,
    path: string,
    options: InternalRequest,
    unauthorized: Response,
  ): Promise<Response> {
    const locks = (globalThis.navigator as Navigator | undefined)?.locks;
    const attempt = async (checkFirst: boolean): Promise<Response> => {
      let latest = unauthorized;
      if (checkFirst) {
        latest = await this.send(method, path, options);
        if (latest.status !== 401) return latest;
      }
      return (await this.refreshSession()) ? this.send(method, path, options) : latest;
    };
    if (!locks) return attempt(false);
    return locks.request(REFRESH_LOCK, () => attempt(true));
  }

  /**
   * refreshSession renews the session cookies. Concurrent callers share one
   * request: refresh tokens rotate with reuse detection, so two parallel
   * refreshes would present the same token twice and revoke the session.
   */
  private refreshSession(): Promise<boolean> {
    this.refreshing ??= this.send('POST', '/auth/refresh', {})
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  /**
   * href is the absolute URL of an API path, with its query, for something
   * the browser fetches itself -- a download link -- with the session
   * cookie and no client in between. Empty and undefined params are left
   * out, as on every request.
   */
  href(path: string, params?: QueryParams): string {
    return this.url(path, params).toString();
  }

  private url(path: string, params?: QueryParams): URL {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    for (const [key, value] of Object.entries(params ?? {})) {
      const values: readonly (QueryParamValue | undefined)[] = Array.isArray(value)
        ? value
        : [value];
      for (const item of values) {
        if (item !== undefined && item !== '') {
          url.searchParams.append(key, String(item));
        }
      }
    }
    return url;
  }

  private send(method: string, path: string, options: InternalRequest): Promise<Response> {
    const { params, body, headers, signal } = options;

    const url = this.url(path, params);
    // A FormData body is a file upload: sent as multipart, with the
    // boundary the browser writes into its own Content-Type.
    const form = body instanceof FormData;

    return fetch(url, {
      method,
      // The session is an HttpOnly cookie, so every call must send credentials.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body === undefined || form ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(body === undefined ? {} : { body: form ? body : JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  }
}

/**
 * wireErrorBody is ApiErrorBody as it actually arrives: a proxy or a crashing
 * server can send anything, so every field is optional at the parse boundary.
 */
interface wireErrorBody {
  error?: Partial<ApiErrorBody['error']>;
}

/** toApiError reads the API error envelope, falling back to the status line. */
async function toApiError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get('X-Request-Id') ?? undefined;
  const retryAfter = Number(response.headers.get('Retry-After'));
  const retryAfterSeconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined;
  try {
    const body = (await response.json()) as wireErrorBody;
    if (body.error?.code) {
      return new ApiError(
        response.status,
        body.error.code,
        body.error.message ?? response.statusText,
        body.error.requestId ?? requestId,
        retryAfterSeconds,
        body.error.target,
      );
    }
  } catch {
    // A non-JSON body (a proxy error page, for instance) falls through.
  }
  return new ApiError(
    response.status,
    'unknown',
    response.statusText,
    requestId,
    retryAfterSeconds,
  );
}
